// Palindromes — curated interview problems with multiple worked approaches.
// Same shape as dsaProblems/matrix.js: concise steps, a "why & when to use" close per
// approach, and an interactive visualizer wherever it clarifies the expansion.

const palindromeProblems = [
  {
    id: 'longest-palindromic-substring',
    title: 'Longest Palindromic Substring',
    difficulty: 'Medium',
    leetcode: { number: 5, url: 'https://leetcode.com/problems/longest-palindromic-substring/' },
    statement: 'Given a string, return the longest substring that is a palindrome.',
    example: {
      input: 's = "babad"',
      output: '"bab"',
      note: '"aba" is also a valid answer — either is accepted, both have length 3.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Check Every Substring)',
        why:
          "Check every one of the O(n²) possible substrings for the palindrome property directly, keeping the " +
          "longest one found. Correct and simple, but each check itself costs O(n), making the whole approach cubic.",
        steps: [
          'For every starting index i and ending index j ≥ i, extract the substring.',
          'Check whether that substring is a palindrome.',
          'If it is, and it\'s longer than the best found so far, update the best.',
          'Return the longest palindromic substring found.',
        ],
        code:
`String longestPalindromeBruteForce(String s) {
    int n = s.length();
    String best = "";
    for (int i = 0; i < n; i++) {
        for (int j = i; j < n; j++) {
            String sub = s.substring(i, j + 1);
            if (isPalindrome(sub) && sub.length() > best.length()) {
                best = sub;
            }
        }
    }
    return best;
}

private boolean isPalindrome(String s) {
    int lo = 0, hi = s.length() - 1;
    while (lo < hi) {
        if (s.charAt(lo++) != s.charAt(hi--)) return false;
    }
    return true;
}`,
        complexity: { time: 'O(n³)', space: 'O(n) per substring check' },
        whenToUse: "Only useful to confirm the problem's meaning directly — the expand-around-center approach below achieves the same result in a fraction of the time.",
      },
      {
        name: 'Approach 2 — Optimized: Expand Around Center',
        why:
          "Every palindrome has a CENTER — either a single character (odd length) or a gap between two " +
          "characters (even length). There are exactly 2n-1 possible centers in a string of length n. Trying " +
          "each one and expanding outward while characters keep matching finds every palindrome in O(n²), " +
          "without ever re-checking a substring from scratch.",
        steps: [
          'For each of the 2n-1 possible centers (n single-character centers, n-1 between-character centers):',
          'Expand outward from that center while the characters on both sides keep matching.',
          'Track the length of the longest palindrome found this way, and its starting position.',
          'Return the substring corresponding to the best center and length found.',
        ],
        visual: {
          arrays: [{ label: 's = "babad"', values: ['b', 'a', 'b', 'a', 'd'] }],
          varNames: [],
          steps: [
            { highlights: { 's = "babad"': [0] }, note: 'Odd center at index 0 (\'b\'): expand — index -1 out of bounds. Best so far: "b" (length 1).' },
            { highlights: { 's = "babad"': [0, 2] }, note: 'Odd center at index 1 (\'a\'): expand — s[0]=\'b\' matches s[2]=\'b\' → "bab" (length 3). Expand further — index -1 out of bounds, stop. New best: "bab".' },
            { highlights: { 's = "babad"': [1, 3] }, note: 'Odd center at index 2 (\'b\'): expand — s[1]=\'a\' matches s[3]=\'a\' → "aba" (length 3). Expand further — s[0]=\'b\' vs s[4]=\'d\', mismatch, stop. Length 3, but NOT strictly greater than the current best (3) — "bab" remains the answer.' },
            { note: 'The remaining centers (indices 3, 4, and all even-length gaps) produce nothing longer. Final answer: "bab".' },
          ],
        },
        code:
`String longestPalindrome(String s) {
    int start = 0, maxLen = 0;
    for (int center = 0; center < s.length(); center++) {
        int len1 = expandAroundCenter(s, center, center);       // odd length
        int len2 = expandAroundCenter(s, center, center + 1);   // even length
        int len = Math.max(len1, len2);
        if (len > maxLen) {
            maxLen = len;
            start = center - (len - 1) / 2;
        }
    }
    return s.substring(start, start + maxLen);
}

private int expandAroundCenter(String s, int left, int right) {
    while (left >= 0 && right < s.length() && s.charAt(left) == s.charAt(right)) {
        left--;
        right++;
    }
    return right - left - 1;
}`,
        complexity: { time: 'O(n²)', space: 'O(1) extra' },
        interviewNote: "Try the \"Longest Palin. Substring\" mode in this pattern's interactive playground to watch every center get tried on any input string.",
        whenToUse: "The standard, expected solution in nearly every interview — simple to explain, easy to code correctly, and fast enough for typical input sizes.",
      },
      {
        name: 'Approach 3 — Further Optimized: Manacher\'s Algorithm',
        why:
          "By inserting separator characters between every letter (turning every palindrome into an ODD-length " +
          "one) and reusing information from palindromes already found, Manacher's Algorithm avoids re-expanding " +
          "from scratch at every center — each character is processed in amortized O(1), giving true O(n) time.",
        steps: [
          'Transform s by inserting a separator (e.g. \'#\') between every character and at both ends — this makes every palindrome, odd or even, have an odd length in the transformed string.',
          'Track a center C and right boundary R of the rightmost palindrome found so far. For each position i within R, its palindrome radius can be initialized using its MIRROR position across C — already computed — instead of starting from scratch.',
          'Attempt to expand further past that initial guess with direct character comparisons, exactly like Approach 2.',
          'Whenever a palindrome expands past R, update C and R. The largest radius found anywhere converts back to the longest palindromic substring of the original string.',
        ],
        code:
`String longestPalindromeManacher(String s) {
    StringBuilder sb = new StringBuilder("#");
    for (char c : s.toCharArray()) sb.append(c).append('#');
    String t = sb.toString();
    int n = t.length();
    int[] P = new int[n];
    int center = 0, right = 0;

    for (int i = 0; i < n; i++) {
        if (i < right) {
            P[i] = Math.min(right - i, P[2 * center - i]);
        }
        while (i - P[i] - 1 >= 0 && i + P[i] + 1 < n && t.charAt(i - P[i] - 1) == t.charAt(i + P[i] + 1)) {
            P[i]++;
        }
        if (i + P[i] > right) {
            center = i;
            right = i + P[i];
        }
    }

    int maxLen = 0, centerIndex = 0;
    for (int i = 0; i < n; i++) {
        if (P[i] > maxLen) { maxLen = P[i]; centerIndex = i; }
    }
    int start = (centerIndex - maxLen) / 2;
    return s.substring(start, start + maxLen);
}`,
        complexity: { time: 'O(n)', space: 'O(n) for the transformed string and radius array' },
        whenToUse: "Rarely required in a standard interview, but a strong follow-up to mention once the O(n²) expand-around-center solution is working — it shows awareness that this problem has a genuinely linear-time solution.",
      },
    ],
  },
  {
    id: 'palindromic-substrings',
    title: 'Palindromic Substrings',
    difficulty: 'Medium',
    leetcode: { number: 647, url: 'https://leetcode.com/problems/palindromic-substrings/' },
    statement: 'Given a string, return the total number of palindromic substrings it contains (different positions count as different substrings, even with identical text).',
    example: {
      input: 's = "aaa"',
      output: '6',
      note: '"a", "a", "a", "aa", "aa", "aaa" — six palindromic substrings total.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Check Every Substring)',
        why:
          "Check every one of the O(n²) substrings directly for the palindrome property, counting how many " +
          "qualify. Correct, but each check costs O(n), making the total cubic.",
        steps: [
          'For every starting index i and ending index j ≥ i, extract the substring (or check it via index range).',
          'Check whether it\'s a palindrome.',
          'If so, increment the count.',
          'Return the total count after checking every possible substring.',
        ],
        code:
`int countSubstringsBruteForce(String s) {
    int n = s.length(), count = 0;
    for (int i = 0; i < n; i++) {
        for (int j = i; j < n; j++) {
            if (isPalindrome(s, i, j)) count++;
        }
    }
    return count;
}

private boolean isPalindrome(String s, int lo, int hi) {
    while (lo < hi) {
        if (s.charAt(lo++) != s.charAt(hi--)) return false;
    }
    return true;
}`,
        complexity: { time: 'O(n³)', space: 'O(1) extra' },
        whenToUse: "Only useful to confirm what counts as a distinct palindromic substring — the expand-around-center approach below counts the exact same thing far more efficiently.",
      },
      {
        name: 'Approach 2 — Optimized: Expand Around Center, Count Every Success',
        why:
          "The exact same expand-around-center technique from Longest Palindromic Substring applies here with " +
          "one small twist: instead of tracking only the LONGEST palindrome found, count EVERY successful " +
          "expansion — each one is a distinct valid palindromic substring in its own right.",
        steps: [
          'For each of the 2n-1 possible centers, expand outward while characters keep matching.',
          'Every successful expansion step (not just the final, longest one) represents one distinct palindromic substring — increment the count each time.',
          'Sum the counts contributed by every center.',
          'Return the total.',
        ],
        visual: {
          arrays: [{ label: 's = "aaa"', values: ['a', 'a', 'a'] }],
          varNames: ['count'],
          steps: [
            { highlights: { 's = "aaa"': [0] }, vars: { count: 1 }, note: 'Odd center at 0: "a" — 1 palindrome found. count=1.' },
            { highlights: { 's = "aaa"': [0, 1] }, vars: { count: 2 }, note: 'Even center between 0,1: "aa" — 1 palindrome found. count=2.' },
            { highlights: { 's = "aaa"': [1] }, vars: { count: 3 }, note: 'Odd center at 1: "a" (length 1) — count=3.' },
            { highlights: { 's = "aaa"': [0, 2] }, vars: { count: 4 }, note: 'Continuing to expand from center 1: "aaa" (length 3) — count=4.' },
            { highlights: { 's = "aaa"': [1, 2] }, vars: { count: 5 }, note: 'Even center between 1,2: "aa" — count=5.' },
            { highlights: { 's = "aaa"': [2] }, vars: { count: 6 }, note: 'Odd center at 2: "a" — count=6. Final answer: 6.' },
          ],
        },
        code:
`int countSubstrings(String s) {
    int count = 0;
    for (int center = 0; center < s.length(); center++) {
        count += expandAndCount(s, center, center);
        count += expandAndCount(s, center, center + 1);
    }
    return count;
}

private int expandAndCount(String s, int left, int right) {
    int count = 0;
    while (left >= 0 && right < s.length() && s.charAt(left) == s.charAt(right)) {
        count++;
        left--;
        right++;
    }
    return count;
}`,
        complexity: { time: 'O(n²)', space: 'O(1) extra' },
        interviewNote: "Try the \"Palindromic Substrings\" mode in this pattern's interactive playground to watch the running count grow with every expansion.",
        whenToUse: "The standard, expected solution — the same expand-around-center machinery as Longest Palindromic Substring, just counting instead of tracking a maximum.",
      },
    ],
  },
];

export default palindromeProblems;
